
import { ActionTypes } from '@/store/actionTypes';
import { defineComponent, PropType } from 'vue';
import { isNullOrUndefined } from '@/shared/helpers';
import AttachTrailerConfirmation from '@/components/AttachTrailerConfirmation.vue';
import FbxInput from '@/components/fbx-input.vue';
import ExpandableBadge from '@/components/ExpandableBadge.vue';
import FbxButton from '@/components/fbx-button.vue';
import ListRefresh from '@/components/ListRefresh.vue';
import LocationTrailerResultCard from '@/components/LocationTrailerResultCard.vue';
import NoTrailersAvailable from '@/components/NoTrailersAvailable.vue';
import NearbyTrailerResult from '@/types/NearbyTrailerResult';
import { TrailerSelectionMethod } from '@/types/TrailerSelectionMethod';
import Trailer from '../../types/Trailer';
import { mapGetters } from 'vuex';

export default defineComponent({
    components: {
        AttachTrailerConfirmation,
        ExpandableBadge,
        FbxButton,
        FbxInput,
        ListRefresh,
        LocationTrailerResultCard,
        NoTrailersAvailable,
    },
    props: {
        isLoading: Boolean,
        isRefreshing: Boolean,
        isOverlayMinimized: Boolean,
        selectedResult: Object as PropType<NearbyTrailerResult | null>,
        searchFilter: {
            type: String,
            default: '',
        },
        filteredTrailers: {
            type: Array as PropType<NearbyTrailerResult[] | null>,
        },
        results: {
            type: Array as PropType<NearbyTrailerResult[]>,
            required: true,
        },
    },
    data(): {
        attachEnabled: boolean;
        isListExpanded: boolean;
        searchTrailer: boolean;
        showConfirm: boolean;
    } {
        return {
            attachEnabled: false,
            isListExpanded: false,
            searchTrailer: false,
            showConfirm: false,
        };
    },
    computed: {
        ...mapGetters({
            trailerAssociationsEnabled: 'trailerAssociationsEnabled',
        }),
        attachButtonText(): string {
            return this.noResults ? 'Attach Trailer' : 'Attach Another Trailer';
        },
        noResults(): boolean | undefined {
            return !this.isLoading && this.results?.length === 0;
        },
        showAttach(): boolean {
            return this.selectedResult !== null;
        },
        shouldHideTrailerListing(): boolean {
            return this.isListExpanded && window.innerWidth > 576;
        },
        trailerNumber(): string {
            return this.selectedResult?.trailers[0].trailerNumber || '';
        },
        trailerCompany(): string {
            return this.selectedResult?.trailers[0].company || '';
        },
        customSidebarStyles() {
            const customStyles: string[] = [];

            if (this.isListExpanded) customStyles.push('expanded-list');
            if (this.isOverlayMinimized) customStyles.push('minimized-list');

            return customStyles;
        },
    },
    watch: {
        selectedResult(val) {
            this.scrollCardintoView(val.trailers[0].id);
        },
    },
    methods: {
        cancelConfirm(): void {
            this.showConfirm = false;
        },
        toggleModal(): void {
            this.showConfirm = true;
        },
        switchExpand(): void {
            if (this.isOverlayMinimized) {
                this.resetOverlayHeight();
                return;
            }
            this.isListExpanded = !this.isListExpanded;
        },
        collapseList() {
            this.isListExpanded = false;
        },
        expandList() {
            this.isListExpanded = true;
        },
        onRowClick(val: NearbyTrailerResult) {
            this.$emit('setSelectedResult', val);
        },
        scrollCardintoView(id: string): void {
            const trailerCard = document.getElementById(id);
            if (trailerCard)
                trailerCard.scrollIntoView({
                    behavior: 'smooth',
                    block: 'nearest',
                });
        },
        navigateBack() {
            this.$router.push({ name: 'DriverLanding' });
        },
        navigateTrailerEnter(): void {
            this.$router.push({ name: 'EnterTrailer' });
        },
        attachTrailer() {
            const trailer = this.selectedResult?.trailers[0];

            if (!isNullOrUndefined(trailer) && trailer instanceof Trailer) {
                this.$store.dispatch(ActionTypes.SET_SELECTED_TRAILER, {
                    trailer,
                    trailerSelectionMethod: TrailerSelectionMethod.select,
                });
                this.$router.push({
                    name: 'TrailerAssessment',
                    params: { trailerNumber: trailer.trailerNumber },
                });
            }
        },
        closeSearchField() {
            this.collapseList();
            this.searchTrailer = false;
            this.setSearchFilter('');
        },
        displaySearchField() {
            this.resetOverlayHeight();
            this.searchTrailer = true;
            this.focusSearchField();
            if (window.innerWidth < 576) this.expandList();
        },
        setSearchFilter(value: string) {
            this.$emit('setSearchFilter', value);
        },
        resetOverlayHeight() {
            this.$emit('resetOverlayHeight');
        },
        async focusSearchField() {
            await this.$nextTick();
            (this.$refs.trailerNumberSearch as HTMLElement).focus();
        },
    },
    mounted() {
        window.addEventListener('resize', () => (this.isListExpanded = false));
    },
    unmounted() {
        window.removeEventListener(
            'resize',
            () => (this.isListExpanded = false)
        );
    },
});
